// buildAddMenuChildren.jsx — sectioned ContextMenu children for KB and node add flows

import React from 'react';

import ContextMenu from 'components/contextmenu/ContextMenu';
import {
  getRecordMenuTypes,
  getRelatedMediaActionVisibility,
  getRelatedRecordMenuTypes,
  PINNED_EVENT_MENU_LABEL,
  PINNED_NOTE_MENU_LABEL,
  PINNED_SKETCH_MENU_LABEL,
  resolvePinnedEventType,
  resolvePinnedNoteType,
  resolvePinnedSketchType,
} from 'components/addmenu/addMenuTypeFilters';
import { getPluralLabel } from 'utilities/Languages';
import { expandAddMenuRows } from 'utilities/typeAppearPolicy';

export const ADD_MENU_ACTION_KEYS = Object.freeze({
  LINK_EXISTING: '__link_existing__',
  IMPORT: '__import__',
  PHOTO: 'CAMERA',
  VIDEO: 'RECORDING',
  UPLOAD: 'ATTACH',
});

/**
 * @param {object} action
 * @returns {string}
 */
export function resolveVendorAttachTypeKey(action) {
  const single = String(action?.typeKey || '').trim();
  if (single) return single;
  const keys = Array.isArray(action?.typeKeys) ? action.typeKeys : [];
  return String(keys[0] || '').trim();
}

/**
 * Builds sectioned menu children for Knowledge Base More.
 *
 * @param {object} options
 * @param {Array<object>} options.typeDefs
 * @param {function} options.onTypeSelect - (typeKey) => void
 * @returns {ReactNode[]}
 */
export function buildKbAddMenuChildren({
  typeDefs = [],
  onTypeSelect,
}) {
  const children = [];
  const recordTypes = expandAddMenuRows(getRecordMenuTypes(typeDefs));

  if (recordTypes.length > 0) {
    children.push(<ContextMenu.Section key="sec-records" label="Add New…" />);
    recordTypes.forEach((row) => {
      const typeKey = String(row?.key || '').trim();
      if (!typeKey) return;
      children.push(
        <ContextMenu.Item
          iconKey={row.iconKey || 'deployed_code'}
          key={`record-${row.menuKey || typeKey}`}
          label={String(row.label || typeKey).trim()}
          onClick={() => onTypeSelect?.(typeKey, {
            appearAsKey: row.appearAsKey,
            initialFields: row.initialFields,
          })}
        />,
      );
    });
  }

  if (children.length > 0) {
    children.push(<ContextMenu.Separator key="sep-photo" />);
  }

  children.push(
    <ContextMenu.Section key="sec-photo" label="Photo & files" />,
    <ContextMenu.Item
      key="photo"
      iconKey="photo_camera"
      label="Take photo"
      onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.PHOTO)}
    />,
    <ContextMenu.Item
      key="video"
      iconKey="videocam"
      label="Take video"
      onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.VIDEO)}
    />,
    <ContextMenu.Item
      key="upload"
      iconKey="attach_file"
      label="Upload file"
      onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.UPLOAD)}
    />,
  );

  return children;
}

/**
 * Builds sectioned menu children for node Link / Add.
 *
 * @param {object} options
 * @param {Array<object>} options.typeDefs
 * @param {string[]|null} [options.relatedTypeKeys]
 * @param {function} options.onLinkExisting
 * @param {function} [options.onImportTypeSelect]
 * @param {boolean} [options.showPurchaseOrderItemsImport]
 * @param {function} [options.onImportPurchaseOrderItems]
 * @param {function} options.onTypeSelect
 * @returns {ReactNode[]}
 */
export function buildNodeAddMenuChildren({
  typeDefs = [],
  relatedTypeKeys = null,
  onLinkExisting,
  onImportTypeSelect,
  showPurchaseOrderItemsImport = false,
  onImportPurchaseOrderItems,
  onTypeSelect,
}) {
  const children = [];
  const pinnedNote = resolvePinnedNoteType(typeDefs);
  const pinnedSketch = resolvePinnedSketchType(typeDefs);
  const pinnedEvent = resolvePinnedEventType(typeDefs);
  const recordTypes = expandAddMenuRows(getRelatedRecordMenuTypes(typeDefs, relatedTypeKeys));
  const mediaVisibility = getRelatedMediaActionVisibility(relatedTypeKeys);
  const showPhotoSection = mediaVisibility.showPhoto
    || mediaVisibility.showVideo
    || mediaVisibility.showUpload;
  const canImportPurchaseOrderItems = showPurchaseOrderItemsImport
    && typeof onImportPurchaseOrderItems === 'function';
  const showImportMenu = (
    (recordTypes.length > 0 && typeof onImportTypeSelect === 'function')
    || canImportPurchaseOrderItems
  );

  // Pin Note / Event with action labels; keep other related types alphabetical.
  const pinnedRecordKeys = new Set();
  const pinnedRecords = [];
  if (mediaVisibility.showNote) {
    pinnedRecordKeys.add(pinnedNote.key);
    pinnedRecords.push({
      key: pinnedNote.key,
      label: PINNED_NOTE_MENU_LABEL,
      iconKey: pinnedNote.iconKey,
    });
  }
  if (mediaVisibility.showSketch && pinnedSketch) {
    pinnedRecordKeys.add(pinnedSketch.key);
    pinnedRecords.push({
      key: pinnedSketch.key,
      label: PINNED_SKETCH_MENU_LABEL,
      iconKey: pinnedSketch.iconKey,
    });
  }
  if (mediaVisibility.showEvent) {
    pinnedRecordKeys.add(pinnedEvent.key);
    pinnedRecords.push({
      key: pinnedEvent.key,
      label: PINNED_EVENT_MENU_LABEL,
      iconKey: pinnedEvent.iconKey,
    });
  }

  const otherAddNewTypes = recordTypes
    .filter((row) => {
      const typeKey = String(row?.key || '').trim().toUpperCase();
      return typeKey && !pinnedRecordKeys.has(typeKey);
    })
    .sort((a, row) => (
      String(a?.label || a?.key || '').localeCompare(String(row?.label || row?.key || ''))
    ));

  const addNewTypes = [...pinnedRecords, ...otherAddNewTypes];

  children.push(
    <ContextMenu.Item
      key="link-existing"
      iconKey="link"
      label="Existing…"
      onClick={() => onLinkExisting?.()}
    />,
  );

  if (addNewTypes.length > 0) {
    children.push(<ContextMenu.Separator key="sep-records" />);
    children.push(<ContextMenu.Section key="sec-records" label="Add New" />);
    addNewTypes.forEach((row) => {
      const typeKey = String(row?.key || '').trim();
      if (!typeKey) return;
      children.push(
        <ContextMenu.Item
          iconKey={row.iconKey || 'deployed_code'}
          key={`record-${row.menuKey || typeKey}`}
          label={String(row.label || typeKey).trim()}
          onClick={() => onTypeSelect?.(typeKey, {
            appearAsKey: row.appearAsKey,
            initialFields: row.initialFields,
          })}
        />,
      );
    });
  }

  if (showPhotoSection) {
    children.push(
      <ContextMenu.Separator key="sep-photo" />,
      <ContextMenu.Section key="sec-photo" label="Photo & files" />,
    );
    if (mediaVisibility.showPhoto) {
      children.push(
        <ContextMenu.Item
          key="photo"
          iconKey="photo_camera"
          label="Take photo"
          onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.PHOTO)}
        />,
      );
    }
    if (mediaVisibility.showVideo) {
      children.push(
        <ContextMenu.Item
          key="video"
          iconKey="videocam"
          label="Take video"
          onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.VIDEO)}
        />,
      );
    }
    if (mediaVisibility.showUpload) {
      children.push(
        <ContextMenu.Item
          key="upload"
          iconKey="attach_file"
          label="Upload files"
          onClick={() => onTypeSelect?.(ADD_MENU_ACTION_KEYS.UPLOAD)}
        />,
      );
    }
  }

  if (showImportMenu) {
    const importChildren = [];

    if (recordTypes.length > 0 && typeof onImportTypeSelect === 'function') {
      recordTypes.forEach((typeDef) => {
        const typeKey = String(typeDef?.key || '').trim();
        if (!typeKey) return;
        importChildren.push(
          <ContextMenu.Item
            iconKey={typeDef.iconKey || 'deployed_code'}
            key={`import-${typeKey}`}
            label={getPluralLabel(typeDef, typeKey)}
            onClick={() => onImportTypeSelect(typeKey)}
          />,
        );
      });
    }

    if (canImportPurchaseOrderItems) {
      importChildren.push(
        <ContextMenu.Item
          key="import-purchase-order-items"
          iconKey="inventory_2"
          label="Purchase order items"
          onClick={() => onImportPurchaseOrderItems()}
        />,
      );
    }

    if (importChildren.length > 0) {
      children.push(
        <ContextMenu.Separator key="sep-import" />,
        <ContextMenu.Item
          key="import"
          iconKey="download"
          label="Import…"
        >
          {importChildren}
        </ContextMenu.Item>,
      );
    }
  }

  return children;
}
